/**
 * Map a run Output artifact into the backend export document shape.
 * Reuses the same table coercion as the on-screen viewer; charts export as data tables.
 */

import type { RunArtifact } from './artifacts';
import { artifactCellText, coerceArtifactTable } from './coerceArtifactTable';
import { asArtifactBlocks, type ArtifactBlock } from './rendering/artifactPrimitiveStyles';

export type StudioExportBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'table'; columns: string[]; rows: string[][] }
  | { type: 'note'; text: string };

export interface StudioExportDocument {
  title: string;
  summary?: string;
  blocks: StudioExportBlock[];
}

function decisionNotes(artifact: RunArtifact): string[] {
  const notes: string[] = [];
  const meta = artifact.body._decision;
  if (meta?.decisionMode === 'PROVISIONAL' || meta?.decisionStatus === 'PROVISIONAL') {
    notes.push('Provisional — results are not evidence-verified.');
  }
  if (meta && Array.isArray(meta.limitations)) {
    for (const line of meta.limitations) {
      if (typeof line === 'string' && line.trim()) notes.push(line.trim());
    }
  }
  return notes;
}

function tableBlock(columns: string[], rows: unknown[][]): StudioExportBlock {
  return {
    type: 'table',
    columns,
    rows: rows.map((row) => columns.map((_, i) => artifactCellText(row[i]))),
  };
}

function appendPrimitiveBlocks(out: StudioExportBlock[], blocks: ArtifactBlock[], depth = 0): void {
  for (const block of blocks) {
    switch (block.type) {
      case 'heading':
        out.push({ type: 'paragraph', text: block.text });
        break;
      case 'text':
        out.push({ type: 'paragraph', text: block.text });
        break;
      case 'callout':
        out.push({ type: 'note', text: block.text });
        break;
      case 'metric':
        out.push({
          type: 'paragraph',
          text: `${block.label}: ${block.value}${block.unit ? ` ${block.unit}` : ''}${
            block.trend ? ` (${block.trend})` : ''
          }`,
        });
        break;
      case 'metrics':
        appendPrimitiveBlocks(out, block.items, depth);
        break;
      case 'table':
        out.push(tableBlock(block.columns, block.rows));
        break;
      case 'chart':
        out.push({
          type: 'paragraph',
          text: `Chart${block.title ? `: ${block.title}` : ''} (${block.chart_type})`,
        });
        out.push(
          tableBlock(
            ['Name', 'Value'],
            block.data.map((d) => [d.name, d.value])
          )
        );
        break;
      case 'section':
        out.push({ type: 'paragraph', text: block.title });
        appendPrimitiveBlocks(out, block.blocks, depth + 1);
        break;
      default:
        break;
    }
  }
}

export function buildArtifactExportDocument(artifact: RunArtifact): StudioExportDocument {
  const blocks: StudioExportBlock[] = [];
  const body = artifact.body;

  if (body.type === 'schedule') {
    if (body.objective?.trim()) {
      blocks.push({ type: 'paragraph', text: `Objective: ${body.objective.trim()}` });
    }
    blocks.push(
      tableBlock(
        ['When', 'Machine', 'Job', 'Duration'],
        body.rows.map((r) => [r.when, r.machine, r.job, r.duration])
      )
    );
    if (body.scoreLine?.trim()) {
      blocks.push({ type: 'paragraph', text: `Scorecard: ${body.scoreLine.trim()}` });
    }
    if (body.notes && body.notes.length > 0) {
      blocks.push({ type: 'note', text: body.notes.join('; ') });
    }
  } else if (body.type === 'document') {
    appendPrimitiveBlocks(blocks, asArtifactBlocks(body.blocks));
  } else if (body.type === 'text') {
    const table = coerceArtifactTable(body.text);
    if (table) {
      blocks.push(tableBlock(table.columns, table.rows));
    } else if (body.text.trim()) {
      blocks.push({ type: 'paragraph', text: body.text });
    }
  } else if (body.type === 'json') {
    const data = body.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const nested = asArtifactBlocks((data as { blocks?: unknown }).blocks);
      if (nested.length > 0) {
        appendPrimitiveBlocks(blocks, nested);
      } else {
        const table = coerceArtifactTable(data);
        if (table) {
          blocks.push(tableBlock(table.columns, table.rows));
        } else {
          try {
            blocks.push({ type: 'paragraph', text: JSON.stringify(data, null, 2) });
          } catch {
            blocks.push({ type: 'paragraph', text: String(data) });
          }
        }
      }
    } else {
      const table = coerceArtifactTable(data);
      if (table) {
        blocks.push(tableBlock(table.columns, table.rows));
      } else {
        try {
          blocks.push({ type: 'paragraph', text: JSON.stringify(data, null, 2) });
        } catch {
          blocks.push({ type: 'paragraph', text: String(data) });
        }
      }
    }
  }

  for (const note of decisionNotes(artifact)) {
    blocks.push({ type: 'note', text: note });
  }

  if (blocks.length === 0) {
    blocks.push({ type: 'paragraph', text: '(Empty output)' });
  }

  return {
    title: artifact.title || 'Studio output',
    summary: artifact.summary,
    blocks,
  };
}
